import React, { useEffect } from "react";
import { type Dispatch } from "@reduxjs/toolkit";
import { connect, type ConnectedProps } from "react-redux";
import { reset } from "../state/slices/surveyPerformanceSlice";
import { type RootState } from "../../../Application/globaltypes/redux";
import ReportingFilter from "../../../../components/reportingFilter/ReportingFilter";
import SurveyPerformanceBody from "./SurveyPerformanceBody";
import { usePerformanceBreadcrumbs } from "../../Common/Hooks/usePerformanceBreadcrumbs";
import ReportingExport from "../../../../components/reportingExport/ReportingExport";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { FeatureFlags } from "../../../../featureFlags";
import { selectIsExportActive } from "features/Reporting/state/export/exportSlice";
import { bindAction, type IWizardStep } from "interfaces";

import "./SurveyPerformance.scss";
import "../../Common/utils/performanceSCSSUtils.scss";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { surveysStateSelector } from "../state/surveyReducer";

export type Props = IWizardStep &
  PropsFromRedux & {
    surveyId: number;
    surveyTitle: string;
  };

export const SurveyPerformance: React.FC<Props> = ({
  surveyId,
  accountId,
  accountType,
  surveyTitle,
  resetState,
  acceptHandlers,
  isExportEnabled,
  publisherId,
  associatedFlows,
}) => {
  const includeAccountsDropdown = useAccountFilterShouldShow(accountId, accountType, true);

  const [filter, setFilter] = usePerformanceFilter(
    {
      includeDistinct: true,
      includeAccountsDropdown,
      accountId,
      supportsSPType: true,
      includeFlows: associatedFlows !== undefined && associatedFlows.length > 0,
    },
    publisherId,
  );
  const reportEnabled = useFeatureFlag(FeatureFlags.SurveyReport);
  const { domElements, registerBreadcrumb, hasBreadcrumbsToDisplay } = usePerformanceBreadcrumbs();

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  useEffect(() => {
    acceptHandlers?.({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className="surveyPerformanceRoot">
      <header className="surveyHeader">
        {hasBreadcrumbsToDisplay ? (
          <div className="breadCrumbs">{domElements}</div>
        ) : (
          <h2 className="surveyTitle">Summary</h2>
        )}
        <div className="surveyActions">
          <ReportingFilter
            currentFilter={filter}
            callback={setFilter}
            includeAccounts={false}
            includeAccountsDropdown={includeAccountsDropdown}
            publisherId={publisherId}
            includeDistinct
            supportsSPType
            flowsData={associatedFlows}
          />
          {isExportEnabled && <ReportingExport />}
        </div>
      </header>
      <div className={"performanceRoot"}>
        <SurveyPerformanceBody
          surveyId={surveyId}
          surveyTitle={surveyTitle}
          registerBreadcrumb={registerBreadcrumb}
          dateFilter={filter}
        />
      </div>
    </div>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    isExportEnabled: selectIsExportActive(state),
    accountId: state.userProfile.accountId,
    accountType: state.userProfile.accountTypeId,
    publisherId: surveysStateSelector(state).base.surveysDetailsReducer.survey.ownerId,
    associatedFlows: surveysStateSelector(state).base.surveysDetailsReducer.settings.flows,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    resetState: bindAction(reset, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SurveyPerformance);
export default ConnectedComponent;
